import { useState } from 'react';
import { quantityText, unitPriceText } from '@/lib/lineDisplay';
import { useTranslation } from 'react-i18next';
import { Link, useLocation, useParams } from 'react-router-dom';
import { useAccount } from '@/api/useAccount';
import { AccountLetterhead } from '@/components/company/AccountLetterhead';
import { DocumentTerms } from '@/components/company/DocumentTerms';
import { Permissions } from '@/auth/permissions';
import { useHasPermission } from '@/auth/RequirePermission';
import { InvoiceActs } from '@/components/invoices/InvoiceActs';
import { InvoiceEmails } from '@/components/invoices/InvoiceEmails';
import { InvoiceReminders } from '@/components/invoices/InvoiceReminders';
import { InvoiceLineEditor } from '@/components/invoices/InvoiceLineEditor';
import { Button } from '@/components/ui/button';
import { ErrorState, LoadingState } from '@/components/ui/states';
import { formatCurrency, formatDate, formatDateOnly } from '@/lib/format';
import { NotFound } from '@/routes/NotFound';
import { InvoiceNotFoundError, useInvoice, type Invoice } from '@/routes/invoices/useInvoices';

/**
 * One invoice (M2-12): its lines, what has been paid and what is still owed,
 * and — for `customers.manage` — its acts (Send invoice, Mark as sent without
 * emailing, Resend, the customer's link, Void). M2-16 adds its payment
 * reminders (with the per-invoice opt-out) and every email about it with its
 * delivery outcome, so a bounce shows here.
 *
 * Every figure is the stored one. A sent invoice's tax froze at send (M2-11),
 * and nothing here recomputes it; a draft carries no tax at all, so its page
 * says tax is worked out at send rather than printing a total somebody would
 * quote. Payment history is M2-15's; this shows `amountPaid` and
 * `balanceDue`, which exist.
 */
export function InvoiceDetail() {
  const { t } = useTranslation();
  const { invoiceId } = useParams<{ invoiceId: string }>();
  const hasPermission = useHasPermission();
  const canAct = hasPermission(Permissions.manageCustomers);
  const invoice = useInvoice(invoiceId);
  const account = useAccount();
  const timeZone = account.data?.timezone;
  const [announcement, setAnnouncement] = useState('');
  // M2-10: a draft's lines open for editing in place. While they are open the
  // acts are hidden, so "Mark as sent" can only ever send SAVED lines.
  const [editing, setEditing] = useState(false);
  const backTo = listLink(useLocation().state);

  if (invoice.error instanceof InvoiceNotFoundError) {
    return <NotFound />;
  }

  if (invoice.isPending) {
    return <LoadingState label={t('invoices.detail.loading')} />;
  }

  if (invoice.isError) {
    return (
      <ErrorState
        title={t('invoices.detail.loadErrorTitle')}
        description={t('invoices.detail.loadErrorDescription')}
        onRetry={() => void invoice.refetch()}
      />
    );
  }

  const i = invoice.data;
  // Instants on the SHOP's clock (the "one clock" rule): the due date beside
  // them is a date on the account's calendar, set from the account's today.
  const on = (value: string) =>
    formatDate(value, { dateStyle: 'medium', ...(timeZone ? { timeZone } : {}) });

  return (
    <section className="space-y-6">
      {/* M2-19 (Q5 = A): the document carries the company it is from. */}
      <AccountLetterhead />
      <div className="space-y-1">
        <Link to={backTo} className="text-sm text-[var(--color-muted-foreground)] hover:underline">
          {t('invoices.detail.back')}
        </Link>
        <h1 className="text-xl font-semibold">
          {t('invoices.numbered', { number: Number(i.number) })}
        </h1>
        <p className="flex flex-wrap items-center gap-x-3 gap-y-1 text-sm">
          <span
            className="rounded-full border border-[var(--color-border)] px-2 py-0.5 text-xs font-medium"
            data-testid="invoice-status"
          >
            {t(`invoices.status.${i.status}`, { defaultValue: i.status })}
          </span>
          <Link to={`/customers/${i.customerId}`} className="hover:underline">
            {i.customerName}
          </Link>
          {i.jobId ? (
            <Link to={`/jobs/${i.jobId}`} className="hover:underline">
              {t('invoices.detail.viewJob')}
            </Link>
          ) : null}
        </p>
        <p className="text-sm text-[var(--color-muted-foreground)]" data-testid="invoice-dates">
          {[
            i.sentAt ? t('invoices.detail.sentOn', { date: on(i.sentAt) }) : null,
            i.dueDate ? t('invoices.dueOn', { date: formatDateOnly(i.dueDate) }) : null,
            i.voidedAt ? t('invoices.detail.voidedOn', { date: on(i.voidedAt) }) : null,
          ]
            .filter(Boolean)
            .join(' · ')}
        </p>
      </div>

      {editing && i.status === 'Draft' ? (
        <InvoiceLineEditor
          invoice={i}
          onDone={(saved) => {
            setEditing(false);
            if (saved) setAnnouncement(t('invoices.edit.saved'));
          }}
        />
      ) : (
        <>
          <InvoiceLines invoice={i} />
          {/* M2-48: what this invoice went out with — the snapshot, never the
              shop's current terms. */}
          <DocumentTerms
            terms={i.terms?.terms}
            warrantyStatement={i.terms?.warrantyStatement}
            termsHeading={t('documentTerms.invoiceTerms')}
            footnote={
              i.terms
                ? t('documentTerms.version', {
                    version: Number(i.terms.version),
                    date: on(i.terms.savedAt),
                  })
                : null
            }
          />
          {canAct && i.status === 'Draft' ? (
            <Button
              type="button"
              variant="outline"
              className="w-full sm:w-auto"
              onClick={() => setEditing(true)}
            >
              {t('invoices.edit.open')}
            </Button>
          ) : null}
          {canAct ? <InvoiceActs invoice={i} onAnnounce={setAnnouncement} /> : null}
          {/* M2-16: what reminders will do, and the emails and what became of them. */}
          <InvoiceReminders invoice={i} canAct={canAct} onAnnounce={setAnnouncement} />
          {/* Kyle 5c: delivery outcomes are the office's — the API sends a
              reader without customers.manage an empty list anyway. */}
          {canAct ? <InvoiceEmails invoice={i} timeZone={timeZone} /> : null}
        </>
      )}

      <p role="status" aria-live="polite" className="sr-only" data-testid="announcement">
        {announcement}
      </p>
    </section>
  );
}

/**
 * Back to the list as it was filtered when this invoice was opened from it
 * (Kyle, 2026-09-28), or the bare list for anyone who arrived another way — a
 * pasted link, a refresh. Navigation state is anything a caller put there, so
 * only a query string is taken from it: never a path, which would make this
 * link go wherever the state said.
 */
function listLink(state: unknown): string {
  const search = (state as { listSearch?: unknown } | null)?.listSearch;
  return typeof search === 'string' && search.startsWith('?') && !search.includes('#')
    ? `/invoices${search}`
    : '/invoices';
}

/** The invoice's lines and totals, read-only — shared with the technician's draft page (M2-10). */
export function InvoiceLines({ invoice }: { invoice: Invoice }) {
  const { t } = useTranslation();
  const draft = invoice.status === 'Draft';

  return (
    <section aria-labelledby="invoice-lines-heading" className="space-y-4">
      <h2 id="invoice-lines-heading" className="text-base font-semibold">
        {t('invoices.detail.lines')}
      </h2>
      {invoice.lines.length === 0 ? (
        <p className="text-sm text-[var(--color-muted-foreground)]">
          {t('invoices.detail.noLines')}
        </p>
      ) : (
        <table className="w-full text-sm">
          <thead className="text-left text-[var(--color-muted-foreground)]">
            <tr>
              <th scope="col" className="py-2 pr-3 font-medium">
                {t('estimates.line.description')}
              </th>
              <th scope="col" className="py-2 pr-3 text-right font-medium">
                {t('estimates.line.quantity')}
              </th>
              <th scope="col" className="hidden py-2 pr-3 text-right font-medium sm:table-cell">
                {t('estimates.line.unitPrice')}
              </th>
              <th scope="col" className="py-2 text-right font-medium">
                {t('estimates.line.total')}
              </th>
            </tr>
          </thead>
          <tbody>
            {invoice.lines.map((line) => (
              <tr
                key={line.id}
                className="border-t border-[var(--color-border)]"
                data-testid="invoice-line"
              >
                <td className="py-2 pr-3">{line.description}</td>
                <td className="py-2 pr-3 text-right tabular-nums">{quantityText(line, t)}</td>
                <td className="hidden py-2 pr-3 text-right tabular-nums sm:table-cell">
                  {unitPriceText(line, t)}
                </td>
                <td className="py-2 text-right tabular-nums">
                  {formatCurrency(Number(line.total))}
                  {Number(line.discount) > 0 ? (
                    <span className="block text-xs text-[var(--color-muted-foreground)]">
                      {t('estimates.line.discountTaken', {
                        amount: formatCurrency(Number(line.discount)),
                      })}
                    </span>
                  ) : null}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      <dl
        className="ml-auto grid max-w-sm grid-cols-[1fr_auto] gap-x-6 gap-y-1 text-sm"
        data-testid="invoice-totals"
      >
        <dt>{t('invoices.totals.subtotal')}</dt>
        <dd className="text-right tabular-nums" data-testid="subtotal">
          {formatCurrency(Number(invoice.subtotal))}
        </dd>
        {draft ? (
          // A draft stores no tax (M2-11): a figure that can still change is
          // one somebody would quote. So no tax, total or balance either —
          // each would be the subtotal wearing a label it has not earned.
          <>
            <dt>{t('invoices.totals.tax')}</dt>
            <dd className="text-right text-[var(--color-muted-foreground)]" data-testid="tax">
              {t('invoices.totals.taxAtSend')}
            </dd>
          </>
        ) : (
          <>
            <dt>{t('invoices.totals.tax')}</dt>
            <dd className="text-right tabular-nums" data-testid="tax">
              {formatCurrency(Number(invoice.tax))}
            </dd>
            <dt className="font-semibold">{t('invoices.totals.total')}</dt>
            <dd className="text-right font-semibold tabular-nums" data-testid="total">
              {formatCurrency(Number(invoice.total))}
            </dd>
            <dt>{t('invoices.totals.amountPaid')}</dt>
            <dd className="text-right tabular-nums" data-testid="amount-paid">
              {formatCurrency(Number(invoice.amountPaid))}
            </dd>
            <dt className="font-semibold">
              {invoice.status === 'Void'
                ? t('invoices.totals.balanceVoid')
                : t('invoices.totals.balanceDue')}
            </dt>
            <dd className="text-right font-semibold tabular-nums" data-testid="balance-due">
              {formatCurrency(Number(invoice.balanceDue))}
            </dd>
          </>
        )}
      </dl>
    </section>
  );
}
